import { useCallback } from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import {
  Button,
  IconBack,
  InformationalScreen,
  ListCard,
  ListItem,
  LoadingModal,
  Modal,
  Text,
} from '@adc/parallax-component-library';
import { useQuery } from '@tanstack/react-query';

import { MainContainer } from '~/components/utility';
import { type RootState } from '~/reducers';
import { getCareTeamConnections } from '~/services/clm';
import { notifyApp } from '~/utilities/appCommunicator';
import { buildTestId } from '~/utilities/buildTestId';
import mediator from '~/utilities/mediator';

const mapStateToProps = ({
  env: { appVersion, osType, osVersion, deviceId },
  auth: { account_id: accountId },
  nav: { query },
}: RootState) => ({
  accountId,
  appVersion,
  osType,
  osVersion,
  deviceId,
  query,
});

const connector = connect(mapStateToProps);

type Props = ConnectedProps<typeof connector>;

const testID = 'care-team-connections-list';

const CareTeamConnectionsList = ({
  accountId,
  appVersion,
  deviceId,
  osType,
  osVersion,
  query,
}: Props) => {
  const { data, isLoading } = useQuery({
    queryKey: [
      'careTeamConnections',
      accountId,
      appVersion,
      osType,
      osVersion,
      deviceId,
    ],
    initialData: [],
    refetchOnWindowFocus: true,
    queryFn: () =>
      getCareTeamConnections({
        patientId: accountId,
        appVersion,
        osType,
        osVersion,
        deviceId,
      }),
  });

  const handleClickBack = useCallback(() => {
    mediator.publish('router:back');
  }, []);

  const handleDone = useCallback(() => {
    notifyApp('adc-webview:connections', { data });
  }, [data]);

  const navigateToAddConnection = useCallback(() => {
    mediator.publish('router:navigate', {
      url: '/add-connection',
      query: { ...query },
    });
    Modal.hide();
  }, [query]);

  const handleAddNewCareTeam = useCallback(() => {
    if (data.length >= 1) {
      if (data.findIndex((d) => d.status === 'connected') !== -1) {
        Modal.show({
          testID: buildTestId({
            testID,
            suffix: ['replace', 'current', 'modal'],
          }),
          header: 'Replace Current Care Team?',
          details:
            'The connection to your current care team will be removed when your new care team accepts your request. Are you sure you want to proceed?',
          buttonLayout: 'row',
          primaryAction: {
            label: 'Proceed',
            onPress: navigateToAddConnection,
          },
          secondaryAction: {
            label: 'Cancel',
            onPress: () => {
              Modal.hide();
            },
          },
        });
      } else {
        Modal.show({
          testID: buildTestId({
            testID,
            suffix: ['replace', 'pending', 'modal'],
          }),
          header: 'Replace Pending Invite?',
          details:
            'Sending a new invite will replace the pending one. Do you want to continue?',
          buttonLayout: 'row',
          primaryAction: {
            label: 'Proceed',
            onPress: navigateToAddConnection,
          },
          secondaryAction: {
            label: 'Cancel',
            onPress: () => {
              Modal.hide();
            },
          },
        });
      }
    } else {
      mediator.publish('router:navigate', {
        url: '/add-connection',
        query: { ...query },
      });
    }
  }, [data, navigateToAddConnection, query]);

  return (
    <MainContainer testID={testID}>
      {(isLoading as boolean) && (
        <LoadingModal
          aria-label="Loading"
          testID={buildTestId({ testID, suffix: ['loading', 'modal'] })}
        />
      )}
      <InformationalScreen testID={buildTestId({ testID, suffix: 'screen' })}>
        <InformationalScreen.Page
          testID={buildTestId({ testID, suffix: 'page' })}
          topNavigationProps={{
            title: 'Connections',
            buttonStart: {
              element: <IconBack testID="nav-back" />,
              label: 'Go Back',
              onPress: handleClickBack,
            },
            testID: 'nav-back',
          }}
        >
          <InformationalScreen.Content>
            <Text marginTop={-8} marginBottom="$2">
              {data.length > 0
                ? "Here's a list of care teams that have access to your information."
                : "You're not connected with any care teams yet."}
            </Text>

            <ListCard>
              {data.map(({ id, name, status }) => (
                <ListItem
                  key={id}
                  title={`${name} - ${id}`}
                  label={
                    status === 'connected'
                      ? 'Current Care Team'
                      : 'Invite Pending With New Care Team'
                  }
                  testID={buildTestId({ testID, suffix: ['list', 'item', id] })}
                />
              ))}
            </ListCard>

            <Button
              variant="secondary"
              label="Add a New Care Team"
              testID={buildTestId({
                testID,
                suffix: ['add', 'new', 'care', 'team', 'button'],
              })}
              onPress={handleAddNewCareTeam}
            />
          </InformationalScreen.Content>
          <InformationalScreen.Footer>
            <Button
              label="Done"
              testID={buildTestId({ testID, suffix: ['done', 'button'] })}
              onPress={handleDone}
            />
          </InformationalScreen.Footer>
        </InformationalScreen.Page>
      </InformationalScreen>
    </MainContainer>
  );
};

export default connector(CareTeamConnectionsList);
